import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";

interface EditTalentDialogProps {
  talent: any;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  partners: { id: string; name: string }[];
  onSave: (updatedTalent: any) => void;
}

export function EditTalentDialog({
  talent,
  isOpen,
  onOpenChange,
  partners,
  onSave
}: EditTalentDialogProps) {
  const { t } = useTranslation(["common"]);
  const { toast } = useToast();
  const [selectedTalent, setSelectedTalent] = useState(talent);
  const [isSaving, setIsSaving] = useState(false);

  // Sync state when talent prop changes
  React.useEffect(() => {
    setSelectedTalent(talent);
  }, [talent]);

  const handleSave = async () => {
    if (!selectedTalent) return;

    setIsSaving(true);
    try {
      const talentId = selectedTalent.id || selectedTalent.talent_id;
      const { error } = await supabase
        .from("talent_profiles")
        .update({
          partner_id: selectedTalent.partner_id || null,
          archived: selectedTalent.archived || false
        })
        .eq("id", talentId);

      if (error) throw error;

      toast({
        title: t("common:status.success"),
        description: t("common:messages.save_success")
      });

      onSave(selectedTalent);
      onOpenChange(false);
    } catch (error) {
      console.error("Error updating talent profile:", error);
      toast({
        title: t("common:status.error"),
        description: t("common:messages.save_error"),
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t("common:actions.edit_talent")}</DialogTitle>
          <DialogDescription>
            {t("common:messages.edit_talent_description")}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="partner">{t("common:labels.partner")}</Label>
            <Select
              value={selectedTalent?.partner_id || "none"}
              onValueChange={(value) =>
                setSelectedTalent({
                  ...selectedTalent,
                  partner_id: value === "none" ? null : value
                })
              }
            >
              <SelectTrigger id="partner">
                <SelectValue placeholder={t("common:actions.select_partner")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  {t("common:labels.no_partner")}
                </SelectItem>
                {partners.map((partner) => (
                  <SelectItem key={partner.id} value={partner.id}>
                    {partner.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="archived">{t("common:status.archived")}</Label>
              <p className="text-sm text-muted-foreground">
                {t("common:messages.archive_talent_explanation")}
              </p>
            </div>
            <Switch
              id="archived"
              checked={selectedTalent?.archived || false}
              onCheckedChange={(checked) =>
                setSelectedTalent({ ...selectedTalent, archived: checked })
              }
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            {t("common:actions.cancel")}
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("common:actions.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
